import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * CUSTOM (HOMYCASA): controller for the variant sketches section.
 *
 * Re-renders its own section through the Section Rendering API whenever the
 * selected variant changes, so the sketches always match the current variant.
 *
 * It listens on `document` rather than the closest section because the sketches
 * live in their own section below the product information, and it only reads the
 * variant id off the event — never the HTML the event carries. That is what makes
 * it work with both pickers: the standard variant-picker refetches the whole
 * product page, while variant-picker-metafields only refetches the
 * product-information section, whose HTML never contains this section.
 *
 * @extends {Component}
 */
class VariantSketches extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {Event} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const sectionId = this.dataset.sectionId;
    if (!detail || !sectionId) return;

    const newProduct = detail.data?.newProduct;

    if (newProduct?.id) {
      this.dataset.productId = String(newProduct.id);
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const variantId = detail.resource?.id;
    if (!variantId) return;

    if (this.dataset.variantId && String(variantId) === String(this.dataset.variantId)) return;

    const baseUrl = newProduct?.url ?? window.location.href;
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));

    sectionRenderer
      .renderSection(sectionId, { url })
      .catch((error) => console.error('Variant sketches update failed:', error));
  };
}

if (!customElements.get('variant-sketches')) {
  customElements.define('variant-sketches', VariantSketches);
}
